import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { AUDIENCE, CATEGORY, recommendationQuestionnaires } from '../../../constants/recommendationQuestionnaires';
import { POST_GRADUATION_OPTION_VALUES } from '../../../constants/general';
import DesktopEvalVersion2 from '../../Pages/Components/DesktopEvalVersion2';
import InsightfulRecommendation from './InsightfulRecommendation';
import { Grid, Typography } from '@material-ui/core';

const INSIGHTFUL_QUESTIONS = [
  { name: 'integrityEmpathyLeadership' },
  { name: 'significantChallenge' },
  { name: 'characterFit' },
  { name: 'strengthsStory' },
  { name: 'additionalInsight' },
];

/**
 * RecommendationQuestionnaire
 * Renders the InsightfulRecommendation section and the main evaluation form for a recommendation.
 * Handles extraction and display of housing, FHP, and plan-based questions.
 */
const RecommendationQuestionnaire = ({
  record,
  responses = {},
  setResponses,
  recommendationText = '',
  setRecommendationText,
  classes,
  audience,
  formExtra = {},
  setFormExtra = (p) => {},
  onInsightfulChange, // optional callback for parent to get values
}) => {
  let recJson = record.recommendation_json || {};
  if (typeof recJson === 'string') {
    try {
      recJson = JSON.parse(recJson);
    } catch (e) {
      recJson = {};
    }
  }

  const isHousing = !!recJson.housing;
  const isFhpHousing = !!recJson.meetsFhpHousingRequirements;

  const getHousingCategories = () => {
    const housingGroup = recommendationQuestionnaires.find((q) => q.audience === AUDIENCE.HOUSING);
    if (!housingGroup) return [];
    return housingGroup.categories.filter((cat) =>
      [CATEGORY.ACADEMIC_CAREER, CATEGORY.INTERPERSONAL, CATEGORY.CHARACTER_LEADERSHIP].includes(cat.name)
    );
  };

  const getFhpQuestions = () => {
    let fhpGroup;
    if (isFhpHousing) {
      fhpGroup = recommendationQuestionnaires.find((q) => q.audience === AUDIENCE.FHP);
    } else {
      fhpGroup = recommendationQuestionnaires.find((q) => q.audience === AUDIENCE.HOUSING);
    }
    if (!fhpGroup) return [];
    const fhpCat = fhpGroup.categories.find((cat) => cat.name === CATEGORY.ADAPTABILITY);
    return fhpCat ? fhpCat.questions : [];
  };

  const getPlanQuestions = () => {
    const planKey = record.post_graduation_plan_selection;
    let audienceKey = null;
    if (planKey === POST_GRADUATION_OPTION_VALUES.FOUR_YEAR_COLLEGE) audienceKey = AUDIENCE.FOUR_YEAR_COLLEGE;
    if (planKey === POST_GRADUATION_OPTION_VALUES.TWO_YEAR_COLLEGE) audienceKey = AUDIENCE.TWO_YEAR_COLLEGE;
    if (planKey === POST_GRADUATION_OPTION_VALUES.MILITARY) audienceKey = AUDIENCE.MILITARY;
    if (planKey === POST_GRADUATION_OPTION_VALUES.WORKFORCE) audienceKey = AUDIENCE.WORKFORCE;
    if (!audienceKey) return [];
    const planGroup = recommendationQuestionnaires.find((q) => q.audience === audienceKey);
    if (!planGroup || !planGroup.categories.length) return [];
    let questions = [];
    planGroup.categories.forEach((cat) => {
      cat.questions.forEach((q) => {
        questions.push({ question: q, category: cat.name });
      });
    });
    return questions;
  };

  const displayQuestions = useMemo(() => {
    let questions = [];
    let sl = 1;
    if (isHousing) {
      getHousingCategories().forEach((cat) => {
        cat.questions.forEach((q) => {
          questions.push({ sl: sl++, question: q, category: cat.name });
        });
      });
    }

    getFhpQuestions().forEach((q) => {
      questions.push({ sl: sl++, question: q, category: CATEGORY.ADAPTABILITY });
    });

    getPlanQuestions().forEach((item) => {
      questions.push({ sl: sl++, question: item.question, category: item.category });
    });
    return questions;
  }, [isHousing, isFhpHousing, record.post_graduation_plan_selection]);

  // InsightfulRecommendation state
  const [insightfulValues, setInsightfulValues] = useState(() => {
    // Optionally initialize from formExtra or record if needed
    const initial = {};
    INSIGHTFUL_QUESTIONS.forEach((q) => {
      initial[q.name] = (formExtra.insightfulRecommendation && formExtra.insightfulRecommendation[q.name]) || '';
    });
    return initial;
  });

  const handleInsightfulChange = (e) => {
    const { name, value } = e.target;
    setInsightfulValues((prev) => {
      const updated = { ...prev, [name]: value };
      if (onInsightfulChange) onInsightfulChange(updated);
      setFormExtra((f) => ({ ...f, insightfulRecommendation: updated }));
      return updated;
    });
  };

  return (
    <>
      <InsightfulRecommendation className={classes.formContainer} values={insightfulValues} onChange={handleInsightfulChange} classes={classes} />
      {isHousing && (
        <DesktopEvalVersion2
          record={record}
          responses={responses}
          setResponses={setResponses}
          recommendationText={recommendationText}
          setRecommendationText={setRecommendationText}
          classes={classes}
          audience={audience}
          formExtra={formExtra}
          setFormExtra={setFormExtra}
          displayQuestions={displayQuestions}
        />
      )}
      <Grid item xs={12} style={{ width: '70%', marginLeft: '295px', marginTop: '50px', textAlign: 'left' }}>
        <Typography variant="body1" paragraph>
          Thank you for your time and input. Your thoughtful input plays a vital role in helping us recognize and support students on their journey to
          success. We truly value your effort and commitment to making a positive impact.
        </Typography>
      </Grid>
    </>
  );
};

RecommendationQuestionnaire.propTypes = {
  record: PropTypes.object.isRequired,
  responses: PropTypes.object,
  setResponses: PropTypes.func,
  recommendationText: PropTypes.string,
  setRecommendationText: PropTypes.func,
  classes: PropTypes.object,
  audience: PropTypes.string,
  formExtra: PropTypes.object,
  setFormExtra: PropTypes.func,
};

export default RecommendationQuestionnaire;
